import {
  FC,
  ReactElement,
  Children,
  isValidElement,
  cloneElement,
  forwardRef
} from 'react';

import {
  RadioGroupProps as MantineRadioGroupProps,
  RadioCardProps,
  Alert,
  StackProps,
  GroupProps,
  Group,
  Stack,
  Input,
  MantineColor,
  RadioProps,
  Radio
} from '@mantine/core';

import { useMergeClassNames } from '../../utils';

import {
  RadioOverrideClassNames,
  RadioGroupOverrideClassNames,
  varsResolver
} from './Radio.styles';

// Create a local Radio component to avoid circular dependency
const CustomRadio = forwardRef<HTMLInputElement, RadioProps>((params, ref) => {
  const mergedClassNames = useMergeClassNames({
    classNames: [RadioOverrideClassNames(undefined, params), params.classNames]
  });

  return (
    <Radio {...params} vars={varsResolver} classNames={mergedClassNames} ref={ref} />
  );
});

export interface RadioOption {
  label: string;
  value: string;
  disabled?: boolean;
  color?: MantineColor;
}

interface RadioGroupProps extends Partial<MantineRadioGroupProps> {
  groupLabel?: string;
  children?: ReactElement<RadioProps> | ReactElement<RadioProps>[] | ReactElement;
  radiosWrapper?: 'Stack' | 'Group';
  radiosWrapperProps?: StackProps | GroupProps;
  options?: RadioOption[];
}

const RadioGroup: FC<RadioGroupProps> = forwardRef<HTMLDivElement, RadioGroupProps>(
  (
    {
      children,
      classNames,
      options,
      groupLabel,
      radiosWrapper,
      radiosWrapperProps,
      ...rest
    },
    ref
  ) => {
    const mergedClassNames = useMergeClassNames({
      classNames: [classNames, RadioGroupOverrideClassNames()]
    });

    const renderOptions = () => {
      if (!options?.length) return null;

      const radioElements = options.map((option) => (
        <CustomRadio
          key={option.value}
          value={option.value}
          label={option.label}
          disabled={option.disabled}
          size={rest.size}
          color={option.color ? option.color : rest.color}
        />
      ));

      if (radiosWrapper === 'Group') {
        return (
          <>
            {groupLabel ? (
              <Group justify="space-between" wrap="nowrap" gap="xs" w="100%">
                <Input.Label mb={0}>{groupLabel}</Input.Label>
                <Group
                  justify="flex-end"
                  wrap="nowrap"
                  {...(radiosWrapperProps as GroupProps)}
                >
                  {radioElements}
                </Group>
              </Group>
            ) : (
              <Group
                {...(radiosWrapperProps as GroupProps)}
                wrap="nowrap"
                gap="xs"
                w="100%"
              >
                {radioElements}
              </Group>
            )}
          </>
        );
      } else {
        return (
          <>
            {groupLabel ? (
              <Stack>
                <Input.Label>{groupLabel}</Input.Label>
                <Stack {...(radiosWrapperProps as StackProps)}>
                  {radioElements}
                </Stack>
              </Stack>
            ) : (
              <Stack {...(radiosWrapperProps as StackProps)}>{radioElements}</Stack>
            )}
          </>
        );
      }
    };

    const renderChildren = () => {
      return Children.map(children as ReactElement<RadioProps>[], (child) => {
        if (isValidElement(child)) {
          return cloneElement(child, {
            ...child.props,
            size: child.props.size || rest.size
          });
        }
        return child;
      });
    };

    const childrenWithProps = children ? (
      renderChildren()
    ) : options?.length ? (
      renderOptions()
    ) : (
      <Alert color="danger">Children / options are missing</Alert>
    );

    return (
      <Radio.Group {...rest} ref={ref} classNames={mergedClassNames}>
        {childrenWithProps}
      </Radio.Group>
    );
  }
);

const RadioIndicator = () => {
  return <Radio.Indicator />;
};

const RadioCard: FC<RadioCardProps> = (props) => {
  return <Radio.Card {...props} />;
};

export { RadioGroup, RadioIndicator, RadioCard };
export type { RadioGroupProps };
